// ============================================================================
// Shared refund modal — one component, two entry contexts (ADR 2026-10-01 Q10).
//
//   - AUTHORIZE_RETURN (from the Returns tab, post-QC): pre-fills nothing in
//     v1 (the backend's authorize endpoint takes amount + destination; the
//     per-line pre-fill lands when the backend returns QC qty/rate on the
//     list endpoint). Posts to POST /oms/returns/<stage_id>/authorize/.
//
//   - RAISE_FROM_SO (from the SO page's "Refund" button, deallocation /
//     standalone): nothing pre-selected. Material lines + service charges.
//     Posts to the existing refund-raise endpoint (NOT this one in v1 —
//     this scaffold wires the UX; integration with the deallocation flow
//     is a follow-on).
//
// Destination: wallet (quick path) or customer bank (dropdown of user's
// UserBankDetails rows + "Add new bank account" sub-modal).
// ============================================================================
import { Alert, Button, Form, Input, InputNumber, Modal, Radio, Select, Space, Table, Tag, Typography, message } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import {
  AddUserBankDetailsInput,
  AuthorizeContextResponse,
  AuthorizeItemRow,
  AuthorizeReturnItem,
  BANK_CHOICES,
  IFSC_REGEX,
  RefundDestination,
  UserBankDetails,
  addUserBankDetails,
  authorizeReturn,
  getAuthorizeContext,
  listUserBankDetails,
} from "../returns.api";
import { RefundDestinationPicker } from "../../_shared/RefundDestinationPicker";
import type { DestinationBlock } from "../../_shared/RefundDestinationPicker/types";

export enum RefundModalContext {
  AUTHORIZE_RETURN = "authorize_return",
  RAISE_FROM_SO = "raise_from_so",
}

interface Props {
  open: boolean;
  onClose: () => void;
  onDone?: () => void;
  context: RefundModalContext;
  stageId?: number;                   // required when context=AUTHORIZE_RETURN
  soId?: number;                      // required for raising from SO page
  userId?: string;                    // bank-details dropdown needs the customer's user id
}

const RefundModal: React.FC<Props> = ({
  open, onClose, onDone, context, stageId, soId, userId,
}) => {
  const [form] = Form.useForm();
  const [destination, setDestination] = useState<RefundDestination>("wallet");
  const [banks, setBanks] = useState<UserBankDetails[]>([]);
  const [addBankOpen, setAddBankOpen] = useState(false);
  // Picker mount only: the bank row is not a mounted Form.Item, so validateFields() never
  // returns it — submit reads the destination from this block instead.
  const [pickerBlock, setPickerBlock] = useState<DestinationBlock | null>(null);
  const [pickerValid, setPickerValid] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Three-column items (original / shortfall / final) for the authorize surface.
  const [ctx, setCtx] = useState<AuthorizeContextResponse | null>(null);
  const [ctxLoading, setCtxLoading] = useState(false);
  const isAuthorize = context === RefundModalContext.AUTHORIZE_RETURN;

  // Load the customer's existing bank accounts whenever `customer` is selected and the user id
  // is known. The AUTHORIZE_RETURN entry doesn't yet pass userId (backend list endpoint doesn't
  // return it per-row) — a follow-on resolves the owner from the stage server-side.
  useEffect(() => {
    if (destination === "customer" && userId) {
      listUserBankDetails(userId).then(setBanks);
    }
  }, [destination, userId]);

  // Pull the three-column context whenever the authorize modal opens for a stage.
  // Prefill each item's "Final" field with suggested_final_qty so one-click confirm is the norm.
  useEffect(() => {
    if (open && isAuthorize && stageId) {
      setCtxLoading(true);
      getAuthorizeContext(stageId).then((data) => {
        setCtx(data ?? null);
        setCtxLoading(false);
        if (data) {
          // Prefill Final qty + Rate per line. Operator can edit either; amount is derived.
          const seed: Record<string, number> = {};
          data.items.forEach((it) => {
            seed[`final_${it.allocation_item_id}`] = Number(it.suggested_final_qty) || 0;
            seed[`rate_${it.allocation_item_id}`] = Number(it.unit_rate) || 0;
          });
          form.setFieldsValue(seed);
        }
      });
    }
    if (!open) {
      setCtx(null);
    }
  }, [open, isAuthorize, stageId, form]);

  // Live total — Σ(final_qty × rate) across every line. Recomputes on each form change so the
  // footer tracks what the operator will post.
  const watchedValues = Form.useWatch([], form);
  const derivedAmount = useMemo(() => {
    if (!ctx) return 0;
    return ctx.items.reduce((sum, it) => {
      const q = Number(watchedValues?.[`final_${it.allocation_item_id}`] ?? it.suggested_final_qty) || 0;
      const r = Number(watchedValues?.[`rate_${it.allocation_item_id}`] ?? it.unit_rate) || 0;
      return sum + q * r;
    }, 0);
  }, [ctx, watchedValues]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      if (context === RefundModalContext.AUTHORIZE_RETURN) {
        if (!stageId) {
          message.error("Missing stage id");
          return;
        }
        // Per-line items — the editable qty and the operator's tax-inclusive rupee rate per unit
        // for each line the modal rendered. Backend recomputes `amount` from Σ(qty × unit_rate),
        // so a stale modal can't credit a different total than the line items imply; it also uses
        // the rate directly on the Zoho CN (ex-tax = rate / (1 + tax%)) so what the operator sees
        // is what the CN emits.
        const items: AuthorizeReturnItem[] | undefined = ctx
          ? ctx.items
              .map((it) => ({
                allocation_item_id: it.allocation_item_id,
                qty: Number(values[`final_${it.allocation_item_id}`] ?? it.suggested_final_qty) || 0,
                unit_rate: Number(values[`rate_${it.allocation_item_id}`] ?? it.unit_rate) || 0,
              }))
              .filter((row) => Number(row.qty) > 0)
          : undefined;
        const amount = (isAuthorize && ctx) ? derivedAmount : Number(values.amount);
        if (!amount || amount <= 0) {
          message.error("Refund total is zero — set a Final qty > 0 on at least one line.");
          return;
        }
        const usePicker = isAuthorize && soId != null;
        if (usePicker && (!pickerBlock || !pickerValid)) {
          message.error("Pick where the refund lands (and the bank account for a customer refund).");
          return;
        }
        const destinationOut = usePicker ? pickerBlock!.destination : values.destination;
        const resp = await authorizeReturn(stageId, {
          amount: amount.toFixed(2),
          destination: destinationOut,
          bank_details_id: usePicker
            ? pickerBlock!.bank_details_id ?? null
            : values.destination === "customer" ? values.bank_details_id : null,
          note: values.note ?? "",
          items,
          ...(usePicker ? { destination_block: pickerBlock! } : {}),
        });
        if (resp) {
          message.success(`Authorized — REFUND_FLOW escalation #${resp.refund_flow_escalation_id}`);
          onDone?.();
        }
      } else {
        // RAISE_FROM_SO integrates with the existing deallocation / standalone refund surface;
        // scaffolded here for the admin's SO-page button. Backend wiring is a follow-on.
        message.warning("Raise-from-SO: backend integration pending (follow-on commit).");
        onDone?.();
      }
    } catch (e) {
      // Form validation errors land in antd's own display — no toast needed.
    } finally {
      setSubmitting(false);
    }
  };

  const title =
    context === RefundModalContext.AUTHORIZE_RETURN
      ? `Authorize refund for return #R-${stageId}`
      : "Raise refund";

  return (
    <>
      <Modal open={open} title={title} onCancel={onClose} onOk={handleSubmit}
             okText="Submit" confirmLoading={submitting} width={isAuthorize ? 720 : 520}>
        <Form form={form} layout="vertical" initialValues={{ destination: "wallet" }}>
          {isAuthorize && (
            <ItemBreakdownTable
              ctx={ctx}
              loading={ctxLoading}
              totalAmount={derivedAmount}
              values={watchedValues}
            />
          )}

          {!isAuthorize && (
            <>
              <Alert
                message="Rate is uncapped — a value above the original line rate shows a warning in the audit entry."
                type="info" showIcon style={{ marginBottom: 16 }}
              />
              <Form.Item name="amount" label="Refund amount (₹)"
                         rules={[{ required: true, message: "Enter the refund amount" }]}>
                <InputNumber style={{ width: "100%" }} min={0.01} step={0.01} precision={2}
                             placeholder="e.g. 1250.00" />
              </Form.Item>
            </>
          )}

          {isAuthorize && soId != null ? (
            // SPEC §5.1: Authorize mount of the unified picker. dont_know is hidden; three
            // branches of SPEC §4.2 are decided server-side off the live escalation state.
            <Form.Item
              name="destination"
              label="Destination"
              rules={[{ required: true }]}
            >
              <div>
                <RefundDestinationPicker
                  mount="authorize"
                  salesOrderId={soId}
                  refundAmountPaise={Math.round((Number(derivedAmount) || 0) * 100)}
                  userId={userId ?? null}
                  savedBankDetails={banks}
                  initial={{ destination, bank_details_id: form.getFieldValue("bank_details_id") }}
                  onChange={(block, valid) => {
                    setPickerBlock(block);
                    setPickerValid(valid);
                    if (block.destination === "customer") {
                      setDestination("customer");
                      form.setFieldsValue({
                        destination: "customer",
                        bank_details_id: block.bank_details_id ?? null,
                      });
                    } else if (block.destination === "wallet") {
                      setDestination("wallet");
                      form.setFieldsValue({ destination: "wallet", bank_details_id: null });
                    }
                  }}
                />
              </div>
            </Form.Item>
          ) : (
            <>
              <Form.Item name="destination" label="Destination"
                         rules={[{ required: true }]}>
                <Radio.Group onChange={(e) => setDestination(e.target.value)}>
                  <Radio.Button value="wallet">Wallet</Radio.Button>
                  <Radio.Button value="customer">Customer bank</Radio.Button>
                </Radio.Group>
              </Form.Item>

              {destination === "customer" && (
                <Form.Item name="bank_details_id" label="Bank account"
                           rules={[{ required: true, message: "Pick the customer's bank account" }]}>
                  <Select
                    placeholder="Select a bank account"
                    options={banks.map((b) => ({
                      value: b.id,
                      label: `${b.bank_name} — ${b.account_holder_name} (…${b.account_number.slice(-4)})`,
                    }))}
                    dropdownRender={(menu) => (
                      <>
                        {menu}
                        <div style={{ padding: 8, borderTop: "1px solid #f0f0f0" }}>
                          <Button type="link" size="small"
                                  onClick={() => setAddBankOpen(true)}>+ Add new bank account</Button>
                        </div>
                      </>
                    )}
                  />
                </Form.Item>
              )}
            </>
          )}

          <Form.Item name="note" label="Note (optional)">
            <Input.TextArea rows={2} placeholder="Context for the audit trail" />
          </Form.Item>
        </Form>
      </Modal>

      <AddBankAccountModal
        open={addBankOpen}
        onClose={() => setAddBankOpen(false)}
        userId={userId}
        onAdded={(row) => {
          setBanks((prev) => [row, ...prev.filter((b) => b.id !== row.id)]);
          form.setFieldValue("bank_details_id", row.id);
          setAddBankOpen(false);
        }}
      />
    </>
  );
};

// ---------------------------------------------------------------------------
// Three-column item breakdown rendered at the top of the Authorize modal.
// Columns: Item · Originally returned · Shortfall · Final (editable).
// "Final" is a plain Form.Item named `final_<ai_id>` — the modal's submit path can read it
// alongside the top-level `amount` field once the backend accepts per-line authorized_qty.
// ---------------------------------------------------------------------------

const ItemBreakdownTable: React.FC<{
  ctx: AuthorizeContextResponse | null;
  loading: boolean;
  totalAmount: number;
  values: Record<string, any> | undefined;
}> = ({ ctx, loading, totalAmount, values }) => {
  const dataSource = ctx?.items ?? [];
  const totals = ctx?.totals;

  if (!loading && dataSource.length === 0) {
    // Nothing to show (older server build without items wiring, or empty stage).
    return null;
  }

  // True when any line's rate had to be prefilled from the pre-GST estimate (live Zoho SO was
  // unreachable). Operator sees a warning so they know to double-check before submit.
  const hasEstimateFallback = dataSource.some((it) => it.rate_source === "estimate_fallback");

  return (
    <div style={{ marginBottom: 16 }}>
      <Typography.Text strong>Items on this return</Typography.Text>
      {totals && Number(totals.shortfall_qty) > 0 && (
        <Tag color="orange" style={{ marginLeft: 8 }}>
          {totals.shortfall_qty} short across pickup + QC
        </Tag>
      )}
      {hasEstimateFallback && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 8 }}
          message="Live Zoho SO was unreachable — rate derived from the estimate line (tax-inclusive, line discount baked in) instead of the invoiced figure. Any order-level discount is NOT included, and a stale / token tax_value on the estimate will show the net rate. Double-check each row against the invoice before authorizing."
        />
      )}
      <Table<AuthorizeItemRow>
        size="small"
        pagination={false}
        rowKey="allocation_item_id"
        loading={loading}
        dataSource={dataSource}
        style={{ marginTop: 8 }}
        columns={[
          {
            title: "Item",
            dataIndex: "product_name",
            key: "product_name",
            render: (v: string) => <Typography.Text>{v}</Typography.Text>,
          },
          {
            title: "Originally returned",
            dataIndex: "originally_returned_qty",
            key: "originally_returned_qty",
            width: 140,
            align: "right",
            render: (v: string) => <Typography.Text type="secondary">{v}</Typography.Text>,
          },
          {
            title: "Shortfall",
            dataIndex: "shortfall_qty",
            key: "shortfall_qty",
            width: 150,
            align: "right",
            render: (_: string, r: AuthorizeItemRow) => {
              const total = Number(r.shortfall_qty) || 0;
              if (total === 0) return <Typography.Text type="secondary">—</Typography.Text>;
              const pickup = Number(r.pickup_shortfall_qty) || 0;
              const qc = Number(r.qc_shortfall_qty) || 0;
              return (
                <span>
                  <Typography.Text type="danger">{total}</Typography.Text>
                  <Typography.Text type="secondary" style={{ fontSize: 11, marginLeft: 4 }}>
                    ({pickup > 0 ? `pickup ${pickup}` : ""}
                    {pickup > 0 && qc > 0 ? " · " : ""}
                    {qc > 0 ? `QC ${qc}` : ""})
                  </Typography.Text>
                </span>
              );
            },
          },
          {
            title: "Final qty",
            key: "final",
            width: 90,
            render: (_, r) => (
              <Form.Item
                name={`final_${r.allocation_item_id}`}
                noStyle
                rules={[
                  {
                    validator: (_rule, val) =>
                      val === undefined || val === null || val === ""
                        ? Promise.reject(new Error("Required"))
                        : Number(val) < 0
                        ? Promise.reject(new Error("≥ 0"))
                        : Number(val) > Number(r.originally_returned_qty)
                        ? Promise.reject(new Error("Can't exceed original"))
                        : Promise.resolve(),
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  max={Number(r.originally_returned_qty)}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            ),
          },
          {
            title: "Rate incl. GST (₹)",
            key: "rate",
            width: 110,
            render: (_, r) => (
              <Form.Item
                name={`rate_${r.allocation_item_id}`}
                noStyle
                rules={[
                  {
                    validator: (_rule, val) =>
                      val === undefined || val === null || val === ""
                        ? Promise.reject(new Error("Required"))
                        : Number(val) < 0
                        ? Promise.reject(new Error("≥ 0"))
                        : Promise.resolve(),
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  step={0.01}
                  precision={2}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            ),
          },
          {
            title: "Subtotal",
            key: "subtotal",
            width: 110,
            align: "right",
            render: (_, r) => {
              const q = Number(values?.[`final_${r.allocation_item_id}`] ?? r.suggested_final_qty) || 0;
              const rt = Number(values?.[`rate_${r.allocation_item_id}`] ?? r.unit_rate) || 0;
              return <Typography.Text strong>{(q * rt).toFixed(2)}</Typography.Text>;
            },
          },
        ]}
        summary={() => (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={5}>
              <Typography.Text strong>Refund total</Typography.Text>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={1} align="right">
              <Typography.Text strong style={{ fontSize: 15 }}>
                ₹ {totalAmount.toFixed(2)}
              </Typography.Text>
            </Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />
      <Typography.Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 4 }}>
        Rate is tax-inclusive per unit (what the customer paid on the invoice, post-GST + discount).
        Edit either Final qty or Rate — the total recomputes and both land on the Zoho CN.
      </Typography.Text>
    </div>
  );
};


// ---------------------------------------------------------------------------
// Sub-modal: Add new bank account. Posts to POST /oms/users/<id>/bank-details/
// ---------------------------------------------------------------------------

export const AddBankAccountModal: React.FC<{
  open: boolean;
  onClose: () => void;
  userId?: string;
  onAdded: (row: UserBankDetails) => void;
}> = ({ open, onClose, userId, onAdded }) => {
  const [form] = Form.useForm<AddUserBankDetailsInput>();
  const [submitting, setSubmitting] = useState(false);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      if (!userId) {
        message.error("User id missing — cannot save bank account");
        return;
      }
      setSubmitting(true);
      const row = await addUserBankDetails(userId, {
        ...values,
        ifsc: values.ifsc.toUpperCase(),
      });
      if (row) {
        onAdded(row);
      }
    } catch {
      // Form validation errors shown inline.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} title="Add bank account" onCancel={onClose} onOk={handleOk}
           okText="Save" confirmLoading={submitting} width={440}>
      <Form form={form} layout="vertical">
        <Form.Item name="bank_name" label="Bank"
                   rules={[{ required: true, message: "Pick the bank" }]}>
          <Select
            showSearch
            placeholder="Select a bank"
            optionFilterProp="label"
            options={BANK_CHOICES}
          />
        </Form.Item>
        <Form.Item name="account_holder_name" label="Account holder name"
                   rules={[{ required: true, max: 128 }]}>
          <Input placeholder="As on the bank record" />
        </Form.Item>
        <Form.Item name="account_number" label="Account number"
                   rules={[{ required: true, max: 32 }]}>
          <Input placeholder="e.g. 00001234567890" />
        </Form.Item>
        <Form.Item name="ifsc" label="IFSC"
                   rules={[
                     { required: true, message: "Enter the IFSC" },
                     { pattern: IFSC_REGEX, message: "IFSC must be 4 letters + 0 + 6 alphanumerics" },
                   ]}>
          <Input placeholder="e.g. HDFC0001234" style={{ textTransform: "uppercase" }} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default RefundModal;
